<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUsersStore, useReservationsStore, useUserGroupsStore, useRestaurantsStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'
import moment from 'moment-timezone'
import axios from '@axios'
import InstantBookingCheckoutDialog from '@core/components/InstantBookingCheckoutDialog.vue'
import ExternalBookingDialog from '@core/components/ExternalBookingDialog.vue'
import InstantBookingSlotEditorDialog from '@core/components/InstantBookingSlotEditorDialog.vue'

const props = defineProps({
  restaurant: {
    type: Object,
    required: true
  },

  // When true, emits 'book-slot' event instead of opening dialog internally
  externalCheckout: {
    type: Boolean,
    default: false
  },

  // When false, hides the "submit request for ideal date/time" link
  showSubmitRequest: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['book-slot', 'slots-loaded'])

const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()
const userGroupsStore = useUserGroupsStore()
const restaurantsStore = useRestaurantsStore()
const { myUser } = storeToRefs(usersStore)
const { restaurantUserGroups } = storeToRefs(userGroupsStore)
const router = useRouter()
const { smAndDown } = useDisplay()

// Check if user is platform admin
const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')

// Check if user is admin for this restaurant (platform admin or restaurant user group member)
const isRestaurantAdmin = computed(() => {
  // Platform admin can manage all restaurants
  if (isPlatformAdmin.value) return true

  // Check if user has admin access to this specific restaurant
  if (!restaurantUserGroups.value || !props.restaurant?._id) return false

  return restaurantUserGroups.value.some(
    rug => rug.restaurant?._id === props.restaurant._id
  )
})

// Alias for backward compatibility in template
const isAdmin = isRestaurantAdmin

// Local state - separate from the global store to avoid affecting DiscoverContent
const localSlots = ref([])
const loading = ref(false)

// Checkout dialog state
const showCheckoutDialog = ref(false)
const showExternalBookingDialog = ref(false)
const selectedSlotId = ref(null)

// Slot editor dialog state (admin)
const showSlotEditorDialog = ref(false)
const editingSlot = ref(null)
const isCloneMode = ref(false)

// Track which slot's radial menu is open
const activeRadialMenuSlotId = ref(null)

// Check if user is platinum member
const isPlatinumMember = computed(() => {
  return myUser.value?.isPlatinum || myUser.value?.platinum
})

const newDraft = async (market, restaurant) => {
  if (!myUser.value) {
    await usersStore.loginNewDummyUser()
  }
  await usersStore.startNewReservation({ restaurant, market })
}

// Get restaurant ID (handle both _id and id)
const restaurantId = computed(() => props.restaurant?._id || props.restaurant?.id)

// Use local slots instead of shared store
const restaurantSlots = computed(() => {
  return localSlots.value.filter(slot =>
    slot.restaurantId === restaurantId.value &&
    slot.status === 'available'
  )
})

// Group slots by date and de-duplicate by time/partySize
const slotsByDate = computed(() => {
  const grouped = {}

  restaurantSlots.value.forEach(slot => {
    const tz = slot.restaurantTimeZone || 'America/New_York'
    const dateKey = moment.tz(slot.date, tz).format('YYYY-MM-DD')
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: dateKey,
        dayName: moment.tz(slot.date, tz).format('dddd'),
        displayDate: moment.tz(slot.date, tz).format('MMMM D, YYYY'),
        shortDate: moment.tz(slot.date, tz).format('MMM D'),
        slots: []
      }
    }
    grouped[dateKey].slots.push(slot)
  })

  // Sort and de-duplicate slots within each date
  Object.values(grouped).forEach(group => {
    // Sort by time first
    group.slots.sort((a, b) => a.time.localeCompare(b.time))

    // De-duplicate by time + partySize (keep first occurrence)
    const seen = new Set()
    group.slots = group.slots.filter(slot => {
      const key = `${slot.time}-${slot.partySize}`
      if (seen.has(key)) {
        return false
      }
      seen.add(key)
      
      return true
    })
  })

  // Return sorted by date
  return Object.values(grouped).sort((a, b) => a.date.localeCompare(b.date))
})

// Format date (e.g., "Sat, Feb 14")
const formatDate = (dateStr, timeZone) => {
  if (!dateStr) return ''
  const tz = timeZone || 'America/New_York'

  return moment.tz(dateStr, tz).format('ddd, MMM D')
}

// Format time to 12-hour format
const formatTime = timeStr => {
  if (!timeStr) return ''
  const parsed = moment(timeStr, ['HH:mm', 'H:mm'], true)
  if (!parsed.isValid()) return timeStr

  return parsed.format('h:mm A')
}

// Handle book slot - open dialog or emit event for parent handling
const handleBookSlot = slot => {
  console.log('🎫 InstantBookingSlotsCompact handleBookSlot called')
  console.log('🎫 slot:', slot)
  console.log('🎫 externalCheckout:', props.externalCheckout)
  const slotId = slot.seoId || slot._id
  console.log('🎫 slotId:', slotId)

  if (props.externalCheckout) {
    // Let parent handle the checkout dialog
    console.log('🎫 Emitting book-slot event with slotId:', slotId)
    emit('book-slot', slotId)
  } else if (slot.slotType === 'external') {
    // Open external booking dialog
    console.log('🎫 Opening external booking dialog')
    selectedSlotId.value = slotId
    showExternalBookingDialog.value = true
  } else {
    // Open dialog internally
    console.log('🎫 Opening dialog internally')
    selectedSlotId.value = slotId
    showCheckoutDialog.value = true
  }
}

// Handle successful booking
const handleBookingSuccess = () => {
  // Dialog handles navigation to /bookings
}

// Admin: Close dialog and navigate to slot management page
const navigateToSlotManagement = () => {
  restaurantsStore.viewingDetailsRestaurant = false
  router.push('/all-platinum-reserved')
}

// Admin: Open editor to edit an existing slot
const openEditSlotDialog = (slot, event) => {
  if (event) event.stopPropagation()
  editingSlot.value = slot
  isCloneMode.value = false
  activeRadialMenuSlotId.value = null
  showSlotEditorDialog.value = true
}

// Admin: Clone an existing slot (opens in create mode with pre-filled data)
const openCloneSlotDialog = (slot, event) => {
  if (event) event.stopPropagation()
  editingSlot.value = slot
  isCloneMode.value = true
  activeRadialMenuSlotId.value = null
  showSlotEditorDialog.value = true
}

// Radial menu handlers
const showRadialMenu = (slotId, event) => {
  event.stopPropagation()
  activeRadialMenuSlotId.value = slotId
}

const hideRadialMenu = () => {
  activeRadialMenuSlotId.value = null
}

// Handle slot saved (refresh local slots)
const handleSlotSaved = async () => {
  if (!restaurantId.value) return

  try {
    const res = await axios.get('/instantBookingSlot/', {
      params: {
        restaurantId: restaurantId.value,
        status: 'available'
      }
    })
    localSlots.value = res.data.slots || []
  } catch (error) {
    console.error('Error refreshing slots:', error)
  }
}

// Handle slot deleted (refresh local slots)
const handleSlotDeleted = async () => {
  await handleSlotSaved()
}

// Fetch slots for the restaurant
const fetchSlots = async () => {
  if (!restaurantId.value) return

  loading.value = true
  try {
    const res = await axios.get('/instantBookingSlot/', {
      params: {
        restaurantId: restaurantId.value,
        status: 'available'
      }
    })
    localSlots.value = res.data.slots || []
    emit('slots-loaded', localSlots.value.length)
  } catch (error) {
    console.error('Error fetching restaurant slots:', error)
    localSlots.value = []
    emit('slots-loaded', 0)
  } finally {
    loading.value = false
  }
}

// Watch for restaurantId changes (e.g., when placeholder is replaced with real restaurant)
watch(restaurantId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    fetchSlots()
  }
}, { immediate: false })

// Fetch slots on mount - use direct API call to avoid overwriting global store state
onMounted(async () => {
  // Load user groups to check admin status
  userGroupsStore.loadInitialData()

  await fetchSlots()
})
</script>

<template>
  <div>
    <div class="instant-bookings-compact">
      <!-- Header Section -->
      <div
        v-if="!isPlatinumMember && myUser"
        class="bookings-header"
      />
      <div>
        <div class="d-flex align-center justify-space-between">
          <p class="mb-0">
            <span
              class="text-content"
              style="font-size: 12px;"
            >Instant Bookings for Elevated Experiences</span>
          </p>
          <!-- Admin: Add Slot Button -->
          <VBtn
            v-if="isAdmin"
            color="primary"
            size="x-small"
            variant="tonal"
            prepend-icon="mdi-plus"
            @click="navigateToSlotManagement"
          >
            Add/Edit Slots
          </VBtn>
        </div>
        <p v-if="showSubmitRequest">
          <span class="description mb-0">
            <span
              class="d-inline-flex align-center cursor-pointer"
              @click="newDraft(restaurant.market, restaurant.name)"
            >
              <svg
                :width="smAndDown ? 16 : 20"
                :height="smAndDown ? 16 : 20"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="mr-1"
              >
                <ellipse
                  cx="8"
                  cy="8"
                  rx="8"
                  ry="8"
                  fill="var(--ds-brand-primary-dark)"
                />
                <path
                  d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                  stroke="white"
                  stroke-width="0.860438"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span class="description text-primary">submit request for ideal date/time</span>

            </span>

          </span>
        </p>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="loading-state"
      >
        <div class="loading-shimmer">
          <div class="shimmer-line" />
          <div class="shimmer-chips">
            <div class="shimmer-chip" />
            <div class="shimmer-chip" />
            <div class="shimmer-chip" />
          </div>
        </div>
      </div>
      <!-- Empty State -->
      <div
        v-else-if="restaurantSlots.length === 0"
        class="empty-state"
      >
        <div class="empty-icon">
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="3"
              y="4"
              width="18"
              height="18"
              rx="2"
              stroke="#CBD5E1"
              stroke-width="1.5"
            />
            <path
              d="M3 9H21"
              stroke="#CBD5E1"
              stroke-width="1.5"
            />
            <path
              d="M8 2V5"
              stroke="#CBD5E1"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M16 2V5"
              stroke="#CBD5E1"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M9 15L15 15"
              stroke="#CBD5E1"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <!-- Regular user: show check back message -->
        <p
          v-if="!isRestaurantAdmin"
          class="empty-subtitle"
        >
          Check back soon for exclusive tables
        </p>
      </div>

      <!-- Slots by Date -->
      <div
        v-else
        class="slots-container"
      >
        <div
          v-for="dateGroup in slotsByDate"
          :key="dateGroup.date"
          class="date-group"
        >
          <!-- Date Header -->
          <!-- Slot Chips -->
          <div class="slots-row">
            <div
              v-for="slot in dateGroup.slots"
              :key="slot._id"
              class="slot-card-wrapper"
              @mouseenter="isAdmin ? showRadialMenu(slot._id, $event) : null"
              @mouseleave="hideRadialMenu"
            >
              <button
                type="button"
                class="slot-card"
                @click.stop="handleBookSlot(slot)"
              >
                <div class="slot-date">
                  {{ formatDate(slot.date, slot.restaurantTimeZone) }}
                </div>
                <div class="slot-time">
                  {{ formatTime(slot.time) }}
                </div>
                <div class="slot-details">
                  <span class="slot-party">
                    <VIcon
                      icon="mdi-account-outline"
                      size="14"
                    />
                    {{ slot.partySize }}
                  </span>
                </div>
              </button>
              <!-- Admin: Radial menu on hover -->
              <Transition name="radial-menu">
                <div
                  v-if="isAdmin && activeRadialMenuSlotId === slot._id"
                  class="slot-radial-menu"
                >
                  <VBtn
                    class="radial-btn radial-btn-edit"
                    icon="mdi-pencil"
                    size="x-small"
                    color="primary"
                    variant="flat"
                    title="Edit slot"
                    @click="openEditSlotDialog(slot, $event)"
                  />
                  <VBtn
                    class="radial-btn radial-btn-clone"
                    icon="mdi-content-copy"
                    size="x-small"
                    color="secondary"
                    variant="flat"
                    title="Clone slot"
                    @click="openCloneSlotDialog(slot, $event)"
                  />
                </div>
              </Transition>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Checkout Dialog (only when not using external checkout) -->
    <InstantBookingCheckoutDialog
      v-if="!externalCheckout"
      v-model="showCheckoutDialog"
      :slot-id="selectedSlotId"
      @success="handleBookingSuccess"
    />

    <!-- External Booking Dialog -->
    <ExternalBookingDialog
      v-model="showExternalBookingDialog"
      :slot-id="selectedSlotId"
    />

    <!-- Admin: Slot Editor Dialog -->
    <InstantBookingSlotEditorDialog
      v-if="isAdmin"
      v-model="showSlotEditorDialog"
      :slot-data="editingSlot"
      :restaurant="restaurant"
      :clone-mode="isCloneMode"
      @saved="handleSlotSaved"
      @deleted="handleSlotDeleted"
    />
  </div>
</template>

<style lang="scss" scoped>
.description {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-xs);
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  color: var(--ds-text-muted);
}

.instant-bookings-compact {
  padding: 0rem 0;
}

// Header
.bookings-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.header-title {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.lightning-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
  border-radius: var(--ds-radius-sm);
  border: 1px solid rgba(251, 191, 36, 0.3);
}

.title-text {
  h3 {
    font-family: var(--ds-font-body);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--ds-text-strong);
    margin: 0;
    line-height: 1.3;
  }

  .subtitle {
    font-family: var(--ds-font-body);
    font-size: 0.8125rem;
    color: #64748B;
    margin: 0.125rem 0 0 0;
    line-height: 1.4;
  }
}

.platinum-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
  border-radius: 100px;
  border: 1px solid rgba(255, 215, 0, 0.3);

  .badge-text {
    font-family: var(--ds-font-body);
    font-size: 0.6875rem;
    font-weight: 500;
    color: #FFD700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

// Loading State
.loading-state {
  padding: 1rem 0;
}

.loading-shimmer {
  .shimmer-line {
    height: 20px;
    width: 40%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: var(--ds-radius-xs);
    margin-bottom: 1rem;
  }

  .shimmer-chips {
    display: flex;
    gap: 0.75rem;
  }

  .shimmer-chip {
    height: 72px;
    width: 100px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: var(--ds-radius-md);
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

// Empty State
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;

  .empty-icon {
    margin-bottom: 1rem;
    opacity: 0.6;
  }

  .empty-title {
    font-family: var(--ds-font-body);
    font-size: 0.9375rem;
    font-weight: 500;
    color: #475569;
    margin: 0 0 0.25rem 0;
  }

  .empty-subtitle {
    font-family: var(--ds-font-body);
    font-size: 0.8125rem;
    color: #94A3B8;
    margin: 0;
  }
}

// Slots Container
.slots-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.date-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.date-header {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #E2E8F0;

  .date-day {
    font-family: var(--ds-font-body);
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--ds-brand-primary-dark);
  }

  .date-full {
    font-family: var(--ds-font-body);
    font-size: 0.8125rem;
    color: #64748B;
  }
}

// Slot Cards
.slots-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.slot-card-wrapper {
  position: relative;
  display: inline-flex;
}

@media (max-width: 600px) {
  .slot-card-wrapper {
    position: relative;
    display: inline-flex;
    width: 78px;
  } 
}

// Radial menu for admin actions
.slot-radial-menu {
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  gap: var(--ds-space-1);
  z-index: 10;
  pointer-events: auto;
}

.radial-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  border-radius: var(--ds-radius-full) !important;
  box-shadow: var(--ds-shadow-sm);
  transition: transform var(--ds-duration-fast) ease, box-shadow var(--ds-duration-fast) ease;

  &:hover {
    transform: scale(1.1);
    box-shadow: var(--ds-shadow-md);
  }

  :deep(.v-icon) {
    font-size: 14px !important;
  }
}

.radial-btn-edit {
  animation: radial-pop-in 0.15s ease forwards;
  animation-delay: 0s;
}

.radial-btn-clone {
  animation: radial-pop-in 0.15s ease forwards;
  animation-delay: 0.05s;
}

@keyframes radial-pop-in {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// Transition for radial menu
.radial-menu-enter-active,
.radial-menu-leave-active {
  transition: opacity var(--ds-duration-fast) ease;
}

.radial-menu-enter-from,
.radial-menu-leave-to {
  opacity: 0;
}

.slot-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  background: var(--ds-surface-card);
  border: 1px solid var(--ds-border-light);
  border-radius: var(--ds-radius-sm);
  cursor: pointer;
  transition: all var(--ds-duration-normal) ease;
  min-width: 90px;

  &:hover:not(.disabled) {
    background: var(--ds-surface-subtle);
    border-color: var(--ds-brand-primary-dark);
    transform: translateY(-1px);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.6;
    background: #F8FAFC;
  }
}

.slot-date {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-xs);
  font-weight: 400;
  color: var(--ds-text-secondary);
}

.slot-time {
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-md);
  font-weight: 600;
  color: var(--ds-text-strong);
}

.slot-details {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--ds-font-body);
  font-size: var(--ds-text-sm);
  color: var(--ds-text-secondary);
}

.slot-party {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

// View All Link
.view-all-link {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #E2E8F0;

  .link-text {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-family: var(--ds-font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--ds-brand-primary-dark);
    text-decoration: none;
    transition: all var(--ds-duration-normal) ease;

    &:hover {
      color: #2D7A73;
      gap: 0.5rem;
    }

    svg {
      transition: transform var(--ds-duration-normal) ease;
    }

    &:hover svg {
      transform: translateX(2px);
    }
  }
}

// Mobile Responsiveness
@media (max-width: 600px) {
  .instant-bookings-compact {
    padding: 0rem 0;
  }

  .bookings-header {
    flex-direction: column;
    gap: 0.75rem;
  }

  .lightning-icon {
    width: 32px;
    height: 32px;
  }

  .title-text h3 {
    font-size: 1rem;
  }

  .title-text .subtitle {
    font-size: 0.75rem;
  }

  .slot-card {
    min-width: 70px;
    padding: 0.5rem 0.625rem;
  }

  .slot-date {
    font-size: 10px;
  }

  .slot-time {
    font-size: var(--ds-text-sm);
  }

  .slot-details {
    font-size: var(--ds-text-xs);
  }
}
</style>
